export type OrganicFamily =
  | 'alkane'
  | 'alkene'
  | 'alkyne'
  | 'alcohol'
  | 'haloalkane'
  | 'aldehyde'
  | 'ketone'
  | 'carboxylic-acid'
  | 'ester'
  | 'amine'
  | 'amide';

export interface OrganicFamilyDefinition {
  id: OrganicFamily;
  name: string;
  functionalGroup: string;
  pattern: string;
  minimumCarbons: number;
  positionMode: 'none' | 'bond' | 'carbon';
  colour: 'sky' | 'coral' | 'mint' | 'sunshine';
}

export interface OrganicMolecule {
  family: OrganicFamilyDefinition;
  carbonCount: number;
  position: number;
  name: string;
  molecularFormula: string;
  condensedFormula: string;
  carbonLabels: string[];
  bondOrders: number[];
}

const ROOTS = ['', 'meth', 'eth', 'prop', 'but', 'pent', 'hex'];

export const ORGANIC_FAMILIES: OrganicFamilyDefinition[] = [
  {
    id: 'alkane',
    name: 'Alkane',
    functionalGroup: 'C–C single bonds',
    pattern: 'CₙH₂ₙ₊₂',
    minimumCarbons: 1,
    positionMode: 'none',
    colour: 'sky',
  },
  {
    id: 'alkene',
    name: 'Alkene',
    functionalGroup: 'C=C',
    pattern: 'CₙH₂ₙ',
    minimumCarbons: 2,
    positionMode: 'bond',
    colour: 'coral',
  },
  {
    id: 'alkyne',
    name: 'Alkyne',
    functionalGroup: 'C≡C',
    pattern: 'CₙH₂ₙ₋₂',
    minimumCarbons: 2,
    positionMode: 'bond',
    colour: 'sunshine',
  },
  {
    id: 'alcohol',
    name: 'Alcohol',
    functionalGroup: 'hydroxyl –OH',
    pattern: 'CₙH₂ₙ₊₂O',
    minimumCarbons: 1,
    positionMode: 'carbon',
    colour: 'mint',
  },
  {
    id: 'haloalkane',
    name: 'Chloroalkane',
    functionalGroup: 'chloro –Cl',
    pattern: 'CₙH₂ₙ₊₁Cl',
    minimumCarbons: 1,
    positionMode: 'carbon',
    colour: 'sunshine',
  },
  {
    id: 'aldehyde',
    name: 'Aldehyde',
    functionalGroup: 'terminal –CHO',
    pattern: 'CₙH₂ₙO',
    minimumCarbons: 1,
    positionMode: 'none',
    colour: 'coral',
  },
  {
    id: 'ketone',
    name: 'Ketone',
    functionalGroup: 'carbonyl >C=O',
    pattern: 'CₙH₂ₙO',
    minimumCarbons: 3,
    positionMode: 'carbon',
    colour: 'coral',
  },
  {
    id: 'carboxylic-acid',
    name: 'Carboxylic acid',
    functionalGroup: 'carboxyl –COOH',
    pattern: 'CₙH₂ₙO₂',
    minimumCarbons: 1,
    positionMode: 'none',
    colour: 'mint',
  },
  {
    id: 'ester',
    name: 'Methyl ester',
    functionalGroup: 'ester –COO–',
    pattern: 'R–COO–CH₃',
    minimumCarbons: 1,
    positionMode: 'none',
    colour: 'mint',
  },
  {
    id: 'amine',
    name: 'Primary amine',
    functionalGroup: 'amino –NH₂',
    pattern: 'CₙH₂ₙ₊₃N',
    minimumCarbons: 1,
    positionMode: 'carbon',
    colour: 'sky',
  },
  {
    id: 'amide',
    name: 'Primary amide',
    functionalGroup: 'amide –CONH₂',
    pattern: 'CₙH₂ₙ₊₁NO',
    minimumCarbons: 1,
    positionMode: 'none',
    colour: 'sunshine',
  },
];

export interface OrganicTransformation {
  id: string;
  from: OrganicFamily;
  to: OrganicFamily;
  label: string;
  reagent: string;
  note: string;
  /** Overrides the target family name when that name is too specific for every starting molecule. */
  targetLabel?: string;
}

export const ORGANIC_TRANSFORMATIONS: OrganicTransformation[] = [
  {
    id: 'alkene-hydration',
    from: 'alkene',
    to: 'alcohol',
    label: 'Hydration',
    reagent: 'H₂O / acid catalyst',
    note: 'Water adds across the carbon–carbon double bond.',
  },
  {
    id: 'alkyne-hydrogenation',
    from: 'alkyne',
    to: 'alkene',
    label: 'Partial hydrogenation',
    reagent: 'H₂ / catalyst',
    note: 'Hydrogen adds across one bond of the carbon–carbon triple bond.',
  },
  {
    id: 'alcohol-oxidation-aldehyde',
    from: 'alcohol',
    to: 'aldehyde',
    label: 'Controlled oxidation',
    reagent: '[O]',
    note: 'A primary alcohol can form an aldehyde under controlled conditions.',
  },
  {
    id: 'aldehyde-oxidation-acid',
    from: 'aldehyde',
    to: 'carboxylic-acid',
    label: 'Further oxidation',
    reagent: '[O]',
    note: 'An aldehyde can oxidise further to a carboxylic acid.',
  },
  {
    id: 'alcohol-elimination',
    from: 'alcohol',
    to: 'alkene',
    label: 'Elimination',
    reagent: 'concentrated acid / heat',
    note: 'Water is removed and a carbon–carbon double bond forms.',
  },
  {
    id: 'alcohol-esterification',
    from: 'alcohol',
    to: 'ester',
    label: 'Esterification',
    reagent: 'carboxylic acid / acid catalyst',
    note: 'An alcohol and carboxylic acid can form an ester and water. The alcohol becomes the alkyl part of the ester name (methanol → methyl …anoate, ethanol → ethyl …anoate).',
    targetLabel: 'Ester',
  },
  {
    id: 'haloalkane-substitution',
    from: 'haloalkane',
    to: 'alcohol',
    label: 'Substitution',
    reagent: 'OH⁻(aq)',
    note: 'The halogen is replaced by a hydroxyl group.',
  },
];

export function allowedPositions(familyId: OrganicFamily, carbonCount: number): number[] {
  const family = ORGANIC_FAMILIES.find((item) => item.id === familyId)!;
  if (family.positionMode === 'none') return [1];
  if (family.positionMode === 'bond') {
    const uniqueBondPositions = Math.floor(carbonCount / 2);
    return Array.from({ length: uniqueBondPositions }, (_, index) => index + 1);
  }
  if (familyId === 'ketone') {
    const finalPosition = Math.floor((carbonCount + 1) / 2);
    return Array.from({ length: Math.max(1, finalPosition - 1) }, (_, index) => index + 2);
  }
  const uniqueCarbonPositions = Math.ceil(carbonCount / 2);
  return Array.from({ length: uniqueCarbonPositions }, (_, index) => index + 1);
}

function subscript(value: number): string {
  const digits = '₀₁₂₃₄₅₆₇₈₉';
  return String(value)
    .split('')
    .map((digit) => digits[Number(digit)])
    .join('');
}

function formula(parts: Array<[string, number]>): string {
  return parts
    .filter(([, count]) => count > 0)
    .map(([element, count]) => `${element}${count === 1 ? '' : subscript(count)}`)
    .join('');
}

function organicName(family: OrganicFamily, carbonCount: number, position: number): string {
  const root = ROOTS[carbonCount];
  if (family === 'alkane') return `${root}ane`;
  if (family === 'alkene') {
    return carbonCount <= 3 ? `${root}ene` : `${root}-${position}-ene`;
  }
  if (family === 'alkyne') {
    return carbonCount <= 3 ? `${root}yne` : `${root}-${position}-yne`;
  }
  if (family === 'alcohol') {
    return carbonCount <= 2 ? `${root}anol` : `${root}an-${position}-ol`;
  }
  if (family === 'haloalkane') {
    return carbonCount <= 2 ? `chloro${root}ane` : `${position}-chloro${root}ane`;
  }
  if (family === 'aldehyde') return `${root}anal`;
  if (family === 'ketone') return `${root}an-${position}-one`;
  if (family === 'carboxylic-acid') return `${root}anoic acid`;
  if (family === 'ester') return `methyl ${root}anoate`;
  if (family === 'amine') {
    return carbonCount <= 2 ? `${root}anamine` : `${root}an-${position}-amine`;
  }
  return `${root}anamide`;
}

function molecularFormula(family: OrganicFamily, carbonCount: number): string {
  if (family === 'alkane')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount + 2],
    ]);
  if (family === 'alkene')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount],
    ]);
  if (family === 'alkyne')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount - 2],
    ]);
  if (family === 'alcohol')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount + 2],
      ['O', 1],
    ]);
  if (family === 'haloalkane')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount + 1],
      ['Cl', 1],
    ]);
  if (family === 'aldehyde' || family === 'ketone')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount],
      ['O', 1],
    ]);
  if (family === 'carboxylic-acid')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount],
      ['O', 2],
    ]);
  if (family === 'ester')
    return formula([
      ['C', carbonCount + 1],
      ['H', 2 * (carbonCount + 1)],
      ['O', 2],
    ]);
  if (family === 'amine')
    return formula([
      ['C', carbonCount],
      ['H', 2 * carbonCount + 3],
      ['N', 1],
    ]);
  return formula([
    ['C', carbonCount],
    ['H', 2 * carbonCount + 1],
    ['N', 1],
    ['O', 1],
  ]);
}

function baseCarbonLabels(carbonCount: number): string[] {
  if (carbonCount === 1) return ['CH₄'];
  return Array.from({ length: carbonCount }, (_, index) =>
    index === 0 || index === carbonCount - 1 ? 'CH₃' : 'CH₂',
  );
}

function structureFor(
  family: OrganicFamily,
  carbonCount: number,
  position: number,
): { labels: string[]; bonds: number[] } {
  const labels = baseCarbonLabels(carbonCount);
  const bonds = Array.from({ length: Math.max(0, carbonCount - 1) }, () => 1);

  if (family === 'alkene') {
    bonds[position - 1] = 2;
    labels[position - 1] = carbonCount === 2 ? 'CH₂' : position === 1 ? 'CH₂' : 'CH';
    labels[position] = position === carbonCount - 1 ? 'CH₂' : 'CH';
  } else if (family === 'alkyne') {
    bonds[position - 1] = 3;
    labels[position - 1] = position === 1 ? 'CH' : 'C';
    labels[position] = position === carbonCount - 1 ? 'CH' : 'C';
  } else if (family === 'alcohol') {
    labels[position - 1] = `${carbonCount === 1 ? 'CH₃' : position === 1 ? 'CH₂' : 'CH'}(OH)`;
  } else if (family === 'haloalkane') {
    labels[position - 1] = `${carbonCount === 1 ? 'CH₃' : position === 1 ? 'CH₂' : 'CH'}Cl`;
  } else if (family === 'aldehyde') {
    labels[carbonCount - 1] = carbonCount === 1 ? 'HCHO' : 'CHO';
  } else if (family === 'ketone') {
    labels[position - 1] = 'C(=O)';
  } else if (family === 'carboxylic-acid') {
    labels[carbonCount - 1] = carbonCount === 1 ? 'HCOOH' : 'COOH';
  } else if (family === 'ester') {
    labels[carbonCount - 1] = carbonCount === 1 ? 'HCOO' : 'COO';
    labels.push('CH₃');
    bonds.push(1);
  } else if (family === 'amine') {
    labels[position - 1] = `${carbonCount === 1 ? 'CH₃' : position === 1 ? 'CH₂' : 'CH'}(NH₂)`;
  } else if (family === 'amide') {
    labels[carbonCount - 1] = carbonCount === 1 ? 'HCONH₂' : 'CONH₂';
  }

  return { labels, bonds };
}

export function buildOrganicMolecule(input: {
  family: OrganicFamily;
  carbonCount: number;
  position?: number;
}): OrganicMolecule {
  const family = ORGANIC_FAMILIES.find((item) => item.id === input.family)!;
  const carbonCount = Math.max(family.minimumCarbons, Math.min(6, input.carbonCount));
  const positions = allowedPositions(family.id, carbonCount);
  const position = positions.includes(input.position ?? positions[0])
    ? (input.position ?? positions[0])
    : positions[0];
  const structure = structureFor(family.id, carbonCount, position);
  const condensedFormula = structure.labels.reduce((text, label, index) => {
    if (index === 0) return label;
    const bond = structure.bonds[index - 1];
    return `${text}${bond === 3 ? '≡' : bond === 2 ? '=' : '–'}${label}`;
  }, '');

  return {
    family,
    carbonCount,
    position,
    name: organicName(family.id, carbonCount, position),
    molecularFormula: molecularFormula(family.id, carbonCount),
    condensedFormula,
    carbonLabels: structure.labels,
    bondOrders: structure.bonds,
  };
}

export function transformationsFrom(family: OrganicFamily): OrganicTransformation[] {
  return ORGANIC_TRANSFORMATIONS.filter((item) => item.from === family);
}

/** Families that support one methyl branch on the main chain. */
export type BranchableFamily = Extract<OrganicFamily, 'alkane' | 'alcohol'>;

export const BRANCHABLE_FAMILIES: readonly BranchableFamily[] = ['alkane', 'alcohol'];

/** Main-chain range for one methyl branch: C3 parents (2-methylpropane) up to C6 parents. */
export const BRANCH_MIN_MAIN_CHAIN = 3;
export const BRANCH_MAX_MAIN_CHAIN = 6;

const BRANCH_ROOTS = ['', 'meth', 'eth', 'prop', 'but', 'pent', 'hex', 'hept'];
const CARBON_VALENCE = 4;
const METHYL_LABEL = 'CH₃';

/** Accepted alternative spellings where IUPAC allows the locant to be omitted. */
const BRANCH_NAME_ALTERNATIVES: Record<string, string[]> = {
  '2-methylpropane': ['methylpropane'],
  '2-methylbutane': ['methylbutane'],
};

export interface BranchedStructure {
  /** Main-chain carbon labels in the learner's numbering (index 0 = C1). */
  labels: string[];
  /** One-based carbon that carries the methyl branch. */
  branchCarbon: number;
  branchLabel: string;
}

export interface BranchedOrganicMolecule {
  family: OrganicFamilyDefinition;
  mainChain: number;
  /** One-based –OH locant on the main chain (always 1 for an alkane, where it is unused). */
  groupPosition: number;
  branchPosition: number;
  totalCarbons: number;
  name: string;
  acceptedNames: string[];
  molecularFormula: string;
  condensedFormula: string;
  structure: BranchedStructure;
  /** Straight-chain molecule with the same molecular formula, used to show isomerism. */
  straightChainIsomer: string;
}

export type BranchRejectionCode =
  | 'unsupported-family'
  | 'unsupported-chain'
  | 'unsupported-group-position'
  | 'unsupported-branch-position'
  | 'lengthens-chain'
  | 'equivalent-position';

export type MethylBranchResult =
  | { ok: true; molecule: BranchedOrganicMolecule }
  | {
      ok: false;
      code: BranchRejectionCode;
      message: string;
      /** Attempted structure, when the positions exist on the chain, so the learner can see it. */
      attempted: BranchedStructure | null;
    };

export function isBranchableFamily(family: OrganicFamily): family is BranchableFamily {
  return (BRANCHABLE_FAMILIES as readonly OrganicFamily[]).includes(family);
}

function lastLegalBranchPosition(
  family: BranchableFamily,
  mainChain: number,
  groupPosition: number,
): number {
  if (family === 'alkane') return Math.floor((mainChain + 1) / 2);
  const hydroxylIsCentral = 2 * groupPosition === mainChain + 1;
  return hydroxylIsCentral ? groupPosition : mainChain - 1;
}

/** Legal, non-equivalent methyl positions after lowest-locant numbering. */
export function allowedBranchPositions(
  family: BranchableFamily,
  mainChain: number,
  groupPosition = 1,
): number[] {
  if (mainChain < BRANCH_MIN_MAIN_CHAIN || mainChain > BRANCH_MAX_MAIN_CHAIN) return [];
  const last = lastLegalBranchPosition(family, mainChain, groupPosition);
  return Array.from({ length: Math.max(0, last - 1) }, (_, index) => index + 2);
}

function branchedStructure(
  family: BranchableFamily,
  mainChain: number,
  groupPosition: number,
  branchCarbon: number,
): BranchedStructure {
  const labels = Array.from({ length: mainChain }, (_, index) => {
    const carbon = index + 1;
    const hasHydroxyl = family === 'alcohol' && carbon === groupPosition;
    const carbonNeighbours =
      (carbon > 1 ? 1 : 0) + (carbon < mainChain ? 1 : 0) + (carbon === branchCarbon ? 1 : 0);
    const hydrogens = CARBON_VALENCE - carbonNeighbours - (hasHydroxyl ? 1 : 0);
    const hydrogenText = hydrogens === 0 ? '' : hydrogens === 1 ? 'H' : `H${subscript(hydrogens)}`;
    return `C${hydrogenText}${hasHydroxyl ? '(OH)' : ''}`;
  });
  return { labels, branchCarbon, branchLabel: METHYL_LABEL };
}

function straightChainName(family: BranchableFamily, carbons: number, groupPosition: number) {
  const root = BRANCH_ROOTS[carbons];
  if (family === 'alkane') return `${root}ane`;
  return carbons <= 2 ? `${root}anol` : `${root}an-${groupPosition}-ol`;
}

function lowestLocant(position: number, chain: number): number {
  return Math.min(position, chain + 1 - position);
}

/** Name of the molecule a learner really built when the methyl sits on a chain end. */
function lengthenedChainName(
  family: BranchableFamily,
  mainChain: number,
  groupPosition: number,
  branchPosition: number,
): string {
  const longer = mainChain + 1;
  const shiftedGroup = branchPosition === 1 ? groupPosition + 1 : groupPosition;
  return straightChainName(family, longer, lowestLocant(shiftedGroup, longer));
}

function branchedName(
  family: BranchableFamily,
  mainChain: number,
  groupPosition: number,
  branchPosition: number,
): string {
  const root = BRANCH_ROOTS[mainChain];
  if (family === 'alkane') return `${branchPosition}-methyl${root}ane`;
  return `${branchPosition}-methyl${root}an-${groupPosition}-ol`;
}

/** Writes the methyl after the carbon, its hydrogens and any –OH: CH(CH₃), C(OH)(CH₃). */
function withBranch(label: string): string {
  return `${label}(${METHYL_LABEL})`;
}

type BranchFailure = Extract<MethylBranchResult, { ok: false }>;

function reject(
  code: BranchRejectionCode,
  message: string,
  attempted: BranchedStructure | null = null,
): BranchFailure {
  return { ok: false, code, message, attempted };
}

/**
 * Builds a C3–C6 main-chain alkane or alcohol with exactly one methyl branch. Fails closed when the
 * branch would make a longer chain, when the numbering is not the lowest-locant numbering, or when
 * the input is outside the reviewed range.
 */
export function buildMethylBranchedMolecule(input: {
  family: OrganicFamily;
  mainChain: number;
  groupPosition?: number;
  branchPosition: number;
}): MethylBranchResult {
  const { family: familyId, mainChain, branchPosition } = input;
  if (!isBranchableFamily(familyId)) {
    return reject(
      'unsupported-family',
      'Methyl branches are reviewed only for alkanes and alcohols in this builder.',
    );
  }
  if (
    !Number.isInteger(mainChain) ||
    mainChain < BRANCH_MIN_MAIN_CHAIN ||
    mainChain > BRANCH_MAX_MAIN_CHAIN
  ) {
    return reject(
      'unsupported-chain',
      `A methyl branch needs a main chain of ${BRANCH_MIN_MAIN_CHAIN} to ${BRANCH_MAX_MAIN_CHAIN} carbons here. A shorter chain has no carbon that can carry a branch without becoming a longer chain.`,
    );
  }
  const groupPosition = familyId === 'alcohol' ? (input.groupPosition ?? 1) : 1;
  if (familyId === 'alcohol' && !allowedPositions('alcohol', mainChain).includes(groupPosition)) {
    return reject(
      'unsupported-group-position',
      'Number the chain so the –OH carbon has the lowest locant before adding a branch.',
    );
  }
  if (!Number.isInteger(branchPosition) || branchPosition < 1 || branchPosition > mainChain) {
    return reject(
      'unsupported-branch-position',
      `The main chain has carbons 1 to ${mainChain}. A branch must sit on one of them.`,
    );
  }

  const structure = branchedStructure(familyId, mainChain, groupPosition, branchPosition);
  if (branchPosition === 1 || branchPosition === mainChain) {
    const realName = lengthenedChainName(familyId, mainChain, groupPosition, branchPosition);
    return reject(
      'lengthens-chain',
      `A methyl on C${branchPosition} simply extends the chain to ${mainChain + 1} carbons. Always choose the longest chain: this molecule is ${realName}, not a branched compound.`,
      structure,
    );
  }

  if (!allowedBranchPositions(familyId, mainChain, groupPosition).includes(branchPosition)) {
    const mirrored = mainChain + 1 - branchPosition;
    const canonical = branchedName(familyId, mainChain, groupPosition, mirrored);
    const reason =
      familyId === 'alcohol'
        ? 'The –OH is in the middle of the chain, so both ends give it the same locant; the methyl must then get the lower number.'
        : 'Numbering from the other end gives the methyl a lower locant.';
    return reject(
      'equivalent-position',
      `C${branchPosition} is the same carbon as C${mirrored} counted from the other end. ${reason} Choose the longest chain and the lowest locants: the name is ${canonical}.`,
      structure,
    );
  }

  const family = ORGANIC_FAMILIES.find((item) => item.id === familyId)!;
  const totalCarbons = mainChain + 1;
  const elements: Array<[string, number]> = [
    ['C', totalCarbons],
    ['H', 2 * totalCarbons + 2],
  ];
  if (familyId === 'alcohol') elements.push(['O', 1]);
  const name = branchedName(familyId, mainChain, groupPosition, branchPosition);

  return {
    ok: true,
    molecule: {
      family,
      mainChain,
      groupPosition,
      branchPosition,
      totalCarbons,
      name,
      acceptedNames: [name, ...(BRANCH_NAME_ALTERNATIVES[name] ?? [])],
      molecularFormula: formula(elements),
      condensedFormula: structure.labels
        .map((label, index) => (index + 1 === branchPosition ? withBranch(label) : label))
        .join('–'),
      structure,
      straightChainIsomer: straightChainName(familyId, totalCarbons, 1),
    },
  };
}

export interface MethylBranchCombination {
  family: BranchableFamily;
  mainChain: number;
  groupPosition: number;
  branchPosition: number;
}

/** Every reviewed one-methyl combination after removing chain-lengthening and mirror positions. */
export function allMethylBranchCombinations(): MethylBranchCombination[] {
  const combinations: MethylBranchCombination[] = [];
  for (const family of BRANCHABLE_FAMILIES) {
    for (let chain = BRANCH_MIN_MAIN_CHAIN; chain <= BRANCH_MAX_MAIN_CHAIN; chain += 1) {
      const groupPositions = family === 'alcohol' ? allowedPositions('alcohol', chain) : [1];
      for (const groupPosition of groupPositions) {
        for (const branchPosition of allowedBranchPositions(family, chain, groupPosition)) {
          combinations.push({ family, mainChain: chain, groupPosition, branchPosition });
        }
      }
    }
  }
  return combinations;
}
